<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

const ROTATE_INTERVAL_MS = 5_000

type DiscountBarState = 'discount' | 'countdown'

interface Props {
    /** Discount percentage, e.g. `15` renders as "−15% Extra". */
    discount: number
    /** ISO date string the discount is valid until — required to show the countdown state. */
    validUntil?: string
    /** Rotates between the discount and a countdown (used by the listing card carousel). */
    rotate?: boolean
    /** Optional campaign accent color (hex). Without it, the bar keeps its default `bg-sale` fill. */
    color?: string
}

const props = withDefaults(defineProps<Props>(), {
    validUntil: undefined,
    rotate: false,
    color: undefined
})

const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

// Rotation needs a target date to count down to, and is skipped entirely for users who asked
// for reduced motion — they see a stable discount instead of an alternating carousel.
const canRotate = computed(() => props.rotate && props.validUntil != null && !prefersReducedMotion.value)

const state = ref<DiscountBarState>('discount')
const displayState = computed<DiscountBarState>(() => (canRotate.value ? state.value : 'discount'))

let rotateHandle: ReturnType<typeof setInterval> | null = null

function stopRotation () {
    if (rotateHandle != null) {
        clearInterval(rotateHandle)
        rotateHandle = null
    }
}

function startRotation () {
    stopRotation()
    rotateHandle = setInterval(() => {
        state.value = state.value === 'discount' ? 'countdown' : 'discount'
    }, ROTATE_INTERVAL_MS)
}

watch(canRotate, (enabled) => {
    if (enabled) {
        startRotation()
        return
    }

    stopRotation()
    state.value = 'discount'
})

// Client-only: the interval must not run during SSR, and `prefers-reduced-motion` can only be
// resolved in the browser anyway — so the server always renders the plain discount state.
onMounted(() => {
    if (canRotate.value) startRotation()
})

onBeforeUnmount(() => {
    stopRotation()
})

const colorProp = computed(() => props.color)
const { barStyle } = useCampaignTheme(colorProp)
</script>

<template>
    <div
        class="relative flex h-7 w-full items-center justify-center overflow-hidden bg-neutral-focus text-xs font-semibold text-neutral-content"
        :style="barStyle"
        data-cy="campaign-discount-bar"
    >
        <!--
            One slide visible at a time, always travelling the same way: the outgoing state
            leaves to the left while the next one enters from the right, so the rotation reads
            as an endless horizontal carousel rather than a back-and-forth toggle. Both slides
            are absolutely positioned so they overlap during the transition instead of
            stretching the bar.
        -->
        <Transition
            enter-active-class="transition-transform duration-500 ease-in-out"
            leave-active-class="transition-transform duration-500 ease-in-out"
            enter-from-class="translate-x-full"
            leave-to-class="-translate-x-full"
        >
            <span
                :key="displayState"
                class="absolute inset-0 flex items-center justify-center gap-1 whitespace-nowrap px-2"
            >
                <template v-if="displayState === 'discount'">
                    <span class="text-base">−{{ discount }}% {{ $t('campaign.discountBar.extra') }}</span>
                </template>

                <template v-else-if="validUntil">
                    <span class="flex items-baseline gap-1">
                        <span class="text-xxs font-normal">{{ $t('campaign.discountBar.endsIn') }}</span>

                        <MiscCountdown
                            :target-date="validUntil"
                            value-class="font-bold text-base"
                            unit-class="text-xxs font-normal"
                        />
                    </span>
                </template>
            </span>
        </Transition>
    </div>
</template>
